import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Button variants.
 *
 * In a `.tokens.ts` file, not `.variants.ts`, for a layout reason worth knowing:
 * `tokens` is one of U22's IDENTITY roles, so this file is invisible to the
 * file-layout rules and sits beside `Button.tsx` in `Button/`. A
 * `Button.variants.ts` would parse as a second CONCEPT in the directory and trip
 * `one-kind-per-directory`.
 *
 * Every class here is a token (U6) and every inline-axis class is logical (U7).
 * The class law reaches `cva()` literals as of P9, so a `pl-` or a `max-w-` in
 * this file is a lint error, not a review comment.
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-sm whitespace-nowrap rounded-md text-label font-medium transition-colors duration-fast ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary-light',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-muted',
        outline: 'border border-border bg-transparent text-foreground hover:bg-secondary',
        ghost: 'bg-transparent text-foreground hover:bg-secondary',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        link: 'bg-transparent text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-md text-caption',
        md: 'h-10 px-lg',
        lg: 'h-12 px-xl text-body',
        // Square, for an icon-only button. Such a button ALWAYS carries an
        // aria-label — jsx-a11y is on at error level and would be right to
        // complain, but the reason is the screen-reader user, not the linter.
        icon: 'h-10 w-10 p-0',
        'icon-sm': 'h-8 w-8 p-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;
